import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { VueConstructor, CreateElement } from 'vue';
import { clx } from '../../lib';

type Classes = {
  root: any;
  flexContainer: any;
  scroller: any;
  fixed: any;
  scrollable: any;
  centered: any;
};

type Variant = 'standard' | 'scrollable' | 'fullWidth';

type IndicatorColor = 'secondary' | 'primary';

type IndicatorStyle = {
  left: string;
  width: string;
};

@Component
export default class ClTabs extends Vue {
  @Prop({
    type: Boolean,
    default: false
  })
  readonly centered!: boolean;

  @Prop({
    type: Object as () => Classes,
    default: () => ({
      root: '',
      flexContainer: '',
      scroller: '',
      fixed: '',
      scrollable: ''
    })
  })
  readonly classes!: Classes;

  @Prop({
    type: [String, Function],
    default: 'div'
  })
  readonly component!: string | VueConstructor;

  @Prop({
    type: String as () => Variant,
    default: 'standard'
  })
  readonly variant!: Variant;

  handleTabClick(event: Event, value: string | number) {
    if (typeof this.$listeners.change === 'function') {
      this.$listeners.change(event, value);
    }
  }

  @Prop({
    type: [String, Number, Boolean]
  })
  readonly value!: any;

  @Prop({
    type: String as () => IndicatorColor,
    default: 'secondary'
  })
  readonly indicatorColor!: IndicatorColor;

  public indicatorStyle: IndicatorStyle = {
    left: '0px',
    width: '0px'
  };

  updateIndicatorStyle() {
    const { tabsMeta, tabMeta } = this.getTabsMeta(this.value);
    let left = '0px';
    if (tabMeta && tabsMeta) {
      left = Math.round(tabMeta.left - tabsMeta.left) + 'px';
    }

    const indicatorStyle = {
      left,
      // May be wrong until the font is loaded.
      width: (tabMeta ? Math.round(tabMeta.width) : 0) + 'px'
    };

    if (
      indicatorStyle.left !== this.indicatorStyle.left ||
      indicatorStyle.width !== this.indicatorStyle.width
    ) {
      this.indicatorStyle = Object.assign(
        {},
        this.indicatorStyle,
        indicatorStyle
      );
    }
  }

  mounted() {
    this.valueToIndex = new Map();
    this.$children.forEach((child, index) => {
      this.valueToIndex.set((child as any).value, index);
    });
    this.updateIndicatorStyle();
  }

  @Watch('value')
  handleValueChange() {
    this.updateIndicatorStyle();
  }

  getTabsMeta(value: any) {
    let tabsMeta;
    const tablist = this.$refs.tablist as Element;
    if (tablist) {
      const rect = tablist.getBoundingClientRect();
      tabsMeta = {
        clientWidth: tablist.clientWidth,
        scrollLeft: tablist.scrollLeft,
        scrollWidth: tablist.scrollWidth,
        left: rect.left,
        right: rect.right
      };
    }

    let tabMeta;
    if (tablist && value !== false) {
      const children = this.$children;
      if (children.length > 0) {
        const tab = children[this.valueToIndex.get(value)];
        tabMeta = tab ? tab.$el.getBoundingClientRect() : null;
      }
    }

    return { tabsMeta, tabMeta };
  }

  valueToIndex!: Map<any, any>;

  render(h: CreateElement) {
    const {
      component: Component,
      classes,
      centered,
      variant,
      $scopedSlots,
      indicatorColor,
      indicatorStyle
    } = this;
    const scrollable = variant === 'scrollable';
    return (
      <Component>
        <div class={clx('cl-tabs__flex-container', classes.flexContainer)}>
          <div
            ref="tablist"
            role="tablist"
            class={clx(
              'cl-tabs__scroller',
              classes.scroller,
              scrollable ? classes.scrollable : classes.fixed
            )}
          >
            <div
              class={clx(
                'cl-tabs__flex-container',
                classes.flexContainer,
                centered && !scrollable && classes.centered
              )}
            >
              {$scopedSlots.default &&
                $scopedSlots.default({
                  tabListeners: {
                    click: this.handleTabClick
                  },
                  selectedValue: this.value
                })}
              <div
                class={clx({
                  'cl-tabs__indicator': true,
                  [`cl-tabs__indicator--color-${indicatorColor}`]: true
                })}
                style={indicatorStyle}
              />
            </div>
          </div>
        </div>
      </Component>
    );
  }
}
